/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/ViewB2BRegistrationView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import LanguageSelector from "../LanguageSelector/index.jsx";
import NotificationBell from "../NotificationBell/index.jsx";
import ViewB2BRegistrationContent from "../ViewB2BRegistrationContent/index.jsx";
import { useTranslation } from "react-i18next";

import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";

import styles from "./ViewB2BRegistrationView.module.css";

/**
 * Full-page B2B registration details on mobile/tablet viewports.
 *
 * @param {Object} props
 * @param {import("react-router").NavigateFunction} props.navigate - For {@link NotificationBell}.
 * @param {Object} props.event - B2B event document.
 * @param {Object} props.appointment - Appointment payload for QR and fields.
 * @param {function} props.onBack - Called when the user taps go back.
 */
const ViewB2BRegistrationView = ({ navigate, event, appointment, onBack }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarRow}>
          <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            aria-label={t("common.goBack")}
          >
            <ChevronLeftIcon className={styles.backIcon} aria-hidden />
            <span>{t("common.goBack")}</span>
          </button>
          <div className={styles.topBarRight}>
            <NotificationBell navigate={navigate} />
            <LanguageSelector />
          </div>
        </div>
      </div>
      <div className={styles.scroll}>
        <div className={styles.inner}>
          <ViewB2BRegistrationContent
            event={event}
            appointment={appointment}
            variant="page"
            onBack={onBack}
            hideMobileBackRow
          />
        </div>
      </div>
    </div>
  );
};

export default ViewB2BRegistrationView;
